import React, { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Container,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import VerifiedIcon from '@mui/icons-material/Verified';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import GroupsIcon from '@mui/icons-material/Groups';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import HotelIcon from '@mui/icons-material/Hotel';
import StorefrontIcon from '@mui/icons-material/Storefront';
import LocalFloristIcon from '@mui/icons-material/LocalFlorist';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import useSEO from '../hooks/useSEO';
import CTAButton from '../Components/buttons/CTAButton';
import seoConfig from '../config/seo/seoConfig';
import heroImage from '../Assets/home-banner-image.png';
import deliveryImage from '../Assets/delivery-image.png';
import aboutImage from '../Assets/about-background-image.png';

const WHATSAPP_MESSAGE =
  'Olá! Quero receber uma proposta para fornecimento de hortaliças frescas, agroecológicas e certificadas para o meu estabelecimento.';

const highlights = [
  { label: 'Produção agroecológica certificada', value: 'Sim' },
  { label: 'Entrega semanal', value: 'Flexível' },
  { label: 'Pedido mínimo', value: 'Pequeno' },
  { label: 'Tempo de prateleira', value: 'Mais alto' },
];

const painPoints = [
  {
    title: 'Produto que chega velho',
    description: 'A hortaliça perde textura, sabor e aparência antes mesmo de ser usada. Isso compromete o prato e aumenta o desperdício.',
  },
  {
    title: 'Falta de regularidade',
    description: 'Alguns fornecedores passam semanas sem entregar o mesmo padrão. A operação acaba precisando improvisar.',
  },
  {
    title: 'Pedido mínimo que não cabe no seu negócio',
    description: 'Quando o volume é pequeno, o fornecedor comum não se adapta. O resultado é estoque exagerado e custo de oportunidade.',
  },
];

const benefits = [
  {
    icon: VerifiedIcon,
    title: 'Produção agroecológica certificada',
    description: 'Cultivo com práticas reais de cuidado, sem agrotóxicos e com rastreabilidade que sustenta confiança.',
  },
  {
    icon: CalendarTodayIcon,
    title: 'Entrega com flexibilidade comercial',
    description: 'Dias de entrega pensados para o seu calendário, sem burocracia e com comunicação rápida pelo WhatsApp.',
  },
  {
    icon: Inventory2Icon,
    title: 'Pequenos volumes, sem perda de qualidade',
    description: 'Atendemos estabelecimentos que precisam comprar pouco e manter a qualidade em cada entrega.',
  },
  {
    icon: LocalShippingIcon,
    title: 'Colheita próxima da entrega',
    description: 'Quanto mais próximo da entrega, maior a frescura, a crocância e a durabilidade da hortaliça.',
  },
  {
    icon: ShieldOutlinedIcon,
    title: 'Segurança alimentar e padrão',
    description: 'Padronizamos aparência, cheiro, textura e acabamento para que o seu cliente reconheça a diferença.',
  },
  {
    icon: HandshakeOutlinedIcon,
    title: 'Relacionamento próximo com o produtor',
    description: 'Você conversa com quem produz, tem resposta ágil e cria um fornecedor de longo prazo com previsibilidade.',
  },
];

const steps = [
  {
    title: '1. Você solicita',
    description: 'Escreve por WhatsApp com o seu perfil, frequência e produtos que quer receber.',
  },
  {
    title: '2. Planejamos juntos',
    description: 'Confirmamos disponibilidade, volume e dias de entrega para evitar operação improvisada.',
  },
  {
    title: '3. Produzimos com cuidado',
    description: 'A produção segue com atenção ao padrão, à higienização e à regularidade.',
  },
  {
    title: '4. Entregamos no seu ritmo',
    description: 'A hortaliça sai do campo com tempo curto de estocagem e chega ao seu estabelecimento pronta para uso.',
  },
  {
    title: '5. Mantemos o relacionamento',
    description: 'Acompanhamos a operação, ajustamos o pedido e construímos um fornecimento contínuo.',
  },
];

const products = [
  { name: 'Alface Crespa', description: 'Folhas macias e de alta presença visual, ideais para saladas, sanduíches e montagem de pratos.' },
  { name: 'Alface Americana', description: 'Textura firme e acabamento uniforme, muito utilizado em hambúrgueres e pratos de cozinha.' },
  { name: 'Alface Lisa', description: 'Mais versátil e elegante, com boa resistência para uso imediato e apresentação delicada.' },
  { name: 'Alface Romana', description: 'Folha longa, firme e sofisticada, excelente para preparos que pedem padrão visual forte.' },
  { name: 'Rúcula', description: 'Aroma marcante, sabor intenso e excelente para pratos que precisam personalidade.' },
  { name: 'Agrião', description: 'Muito usado na culinária, com frescor e presença que elevam qualquer composição.' },
  { name: 'Coentro', description: 'Perfume forte e sabor muito reconhecido, essencial para a cozinha brasileira.' },
  { name: 'Rabanete', description: 'Textura crocante e visual diferenciado, ideal para produtos com identidade.' },
];

const sectors = [
  { icon: RestaurantIcon, title: 'Restaurantes', description: 'Fornecimento com consistência para cozinha e operação diária.' },
  { icon: HotelIcon, title: 'Hotéis', description: 'Padrão elevado para buffet, room service e áreas de gastronomia.' },
  { icon: StorefrontIcon, title: 'Mercados', description: 'Qualidade e volume equilibrado para reposição regular.' },
  { icon: LocalFloristIcon, title: 'Hortifrutis', description: 'Hortaliças com boa apresentação e boa durabilidade de prateleira.' },
  { icon: AgricultureIcon, title: 'Hamburguerias', description: 'Alfaces e folhas com consistência visual e presença forte no prato.' },
  { icon: GroupsIcon, title: 'Bistrôs', description: 'Produtos diferenciados que valorizam a experiência gastronômica.' },
];

const testimonials = [
  {
    quote: 'A gente precisa de fornecedor que não falhe. A Verde Musgo entrega com previsibilidade, qualidade e comunicação simples.',
    name: 'Marcos T.',
    role: 'Chef de cozinha',
    place: 'Embu-Guaçu',
  },
  {
    quote: 'O diferencial é a regularidade. Nunca fica improvável, e a hortaliça chega fresca e com boa aparência.',
    name: 'Ana L.',
    role: 'Gestora de mercado',
    place: 'Zona Oeste',
  },
  {
    quote: 'Compramos pouco e recebemos bem. Isso é raro no mercado, e a parceria faz diferença para o nosso dia a dia.',
    name: 'Rodrigo P.',
    role: 'Responsável de bistrô',
    place: 'Zona Sul',
  },
];

const faqItems = [
  { question: 'Vocês conseguem entregar toda semana?', answer: 'Sim. A operação é estruturada para entregas recorrentes com previsibilidade e boa comunicação.' },
  { question: 'E se faltar produto em alguma semana?', answer: 'Acompanhamos a demanda e buscamos reequilibrar o abastecimento com antecedência, mantendo transparência.' },
  { question: 'Posso comprar pouco?', answer: 'Sim. Trabalhamos com pequenos volumes para que o estabelecimento não precise aceitar pedidos grandes demais.' },
  { question: 'O preço vale a pena?', answer: 'O valor considera frescura, padrão, menor desperdício e confiabilidade. Muitas vezes, o custo real é menor do que o impacto de produto ruim.' },
  { question: 'É realmente agroecológico?', answer: 'Sim. A produção é conduzida com base em práticas agroecológicas, sem agrotóxicos e com compromisso com a qualidade.' },
  { question: 'Entregam na minha região?', answer: 'Atendemos Embu-Guaçu e regiões sul e oeste de São Paulo. A resposta depende do volume e da frequência da demanda.' },
  { question: 'Há pedido mínimo?', answer: 'Não trabalhamos com grandes pedidos mínimos. O foco é permitir que negócios menores tenham acesso à qualidade.' },
  { question: 'Como funciona a primeira entrega?', answer: 'Você pode iniciar com uma conversa simples pelo WhatsApp e, em seguida, definimos volume, frequência e dia de entrega.' },
  { question: 'As hortaliças têm boa durabilidade?', answer: 'Sim. A colheita próxima da entrega e o cuidado na logística favorecem melhor prateleira e menos desperdício.' },
  { question: 'Vocês entregam para cozinhas industriais?', answer: 'Sim. A operação é pensada para atender diferentes escalas, desde pequenos pontos até demandas maiores.' },
  { question: 'Os produtos chegam embalados?', answer: 'A logística é pensada para preservar a apresentação e a integridade das folhas até a chegada ao estabelecimento.' },
  { question: 'Vocês fazem notas fiscais?', answer: 'Sim. O processo comercial segue com atenção à formalização necessária para a operação do cliente.' },
  { question: 'Quais dias de entrega vocês têm?', answer: 'As datas são definidas conforme a sua rotina, a frequência e a disponibilidade regional.' },
  { question: 'Posso conhecer a produção?', answer: 'Sim. O relacionamento direto com o produtor permite isso quando houver interesse.' },
  { question: 'Atendem também pessoa física?', answer: 'Nossa proposta principal é B2B, mas avaliamos demandas específicas conforme a logística.' },
];

const sectionTitleSx = {
  fontFamily: '"Reem Kufi", sans-serif',
  fontSize: { xs: 'clamp(1.75rem, 4vw, 2.25rem)', md: 'clamp(2.2rem, 4vw, 2.85rem)' },
  fontWeight: 700,
  color: '#1B422E',
  mb: 2,
};

const HortalicasPage = () => {
  const seo = useSEO();
  const [expandedFaq, setExpandedFaq] = useState(false);

  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': 'https://www.verdemusgonatural.com.br/#organization',
        name: 'Verde Musgo Natural',
        url: 'https://www.verdemusgonatural.com.br',
        logo: 'https://www.verdemusgonatural.com.br/logo.png',
        description: 'Produtor agroecológico de hortaliças frescas, certificadas e entregues direto do produtor para estabelecimentos em São Paulo.',
        sameAs: [
          'https://instagram.com/verdemusgo.natural',
          'https://facebook.com/verdemusgo.natural',
        ],
      },
      {
        '@type': 'LocalBusiness',
        '@id': 'https://www.verdemusgonatural.com.br/hortalicas#localbusiness',
        name: 'Verde Musgo Natural',
        url: 'https://www.verdemusgonatural.com.br/hortalicas',
        image: 'https://www.verdemusgonatural.com.br/home-banner-image.png',
        telephone: seoConfig.company.whatsapp,
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Embu-Guaçu',
          addressRegion: 'SP',
          addressCountry: 'BR',
        },
        areaServed: ['Embu-Guaçu', 'Zona Sul de São Paulo', 'Zona Oeste de São Paulo'],
        description: 'Fornecedor de hortaliças agroecológicas, certificadas e frescas para restaurantes, hotéis, mercados e cozinhas industriais.',
        priceRange: '$$',
        openingHoursSpecification: [
          {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
            opens: '07:00',
            closes: '18:00',
          },
        ],
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqItems.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
          },
        })),
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Início', item: 'https://www.verdemusgonatural.com.br/' },
          { '@type': 'ListItem', position: 2, name: 'Hortaliças', item: 'https://www.verdemusgonatural.com.br/hortalicas' },
        ],
      },
    ],
  };

  return (
    <Box sx={{ backgroundColor: '#f7f4ea', color: '#173324' }}>
      {seo}
      <script type="application/ld+json">{JSON.stringify(structuredData)}</script>

      <Box id="hero" sx={{ background: 'linear-gradient(135deg, #163b22 0%, #285b2d 55%, #3f7f40 100%)', color: 'white', py: { xs: 8, md: 12 }, position: 'relative', overflow: 'hidden' }}>
        <Box aria-hidden="true" sx={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at top right, rgba(255,255,255,0.16), transparent 28%)' }} />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={7}>
              <Stack spacing={3} sx={{ maxWidth: 680 }}>
                <Chip label="Fornecimento estável • produção agroecológica certificada" sx={{ alignSelf: 'flex-start', backgroundColor: 'rgba(255,255,255,0.14)', color: 'white', borderRadius: '999px', px: 1.5, py: 1.25, fontWeight: 600 }} />
                <Typography component="h1" sx={{ fontFamily: '"Reem Kufi", sans-serif', fontSize: { xs: 'clamp(2.2rem, 5vw, 3.4rem)', md: 'clamp(2.8rem, 4.2vw, 4.2rem)' }, fontWeight: 700, lineHeight: 1.1 }}>
                  Hortaliças frescas, certificadas e entregues direto do produtor.
                </Typography>
                <Typography sx={{ fontSize: { xs: '1rem', md: '1.1rem' }, color: 'rgba(255,255,255,0.86)', lineHeight: 1.8, maxWidth: 620 }}>
                  Para restaurantes, hotéis, mercados, bistrôs e cozinhas industriais em Embu-Guaçu, Zona Sul e Zona Oeste de São Paulo. Entrega recorrente, pequeno volume, padrão elevado e resposta rápida pelo WhatsApp.
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <CTAButton text="Solicitar tabela de preços" type="whatsapp" variant="whatsapp" message={WHATSAPP_MESSAGE} size="large" />
                  <CTAButton text="Ver como funciona" href="#como-funciona" type="link" variant="outlined" size="large" />
                </Stack>
                <Grid container spacing={1.5} sx={{ mt: 1 }}>
                  {highlights.map((item) => (
                    <Grid item xs={12} sm={6} key={item.label}>
                      <Box sx={{ border: '1px solid rgba(255,255,255,0.18)', borderRadius: 3, backgroundColor: 'rgba(255,255,255,0.08)', p: 2 }}>
                        <Typography sx={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.72)', textTransform: 'uppercase', letterSpacing: '0.16em', mb: 0.5 }}>{item.label}</Typography>
                        <Typography sx={{ fontSize: '1.05rem', fontWeight: 700 }}>{item.value}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Stack>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box sx={{ position: 'relative' }}>
                <Box sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: '0 24px 100px rgba(0,0,0,0.28)', border: '1px solid rgba(255,255,255,0.18)' }}>
                  <img src={heroImage} alt="Hortaliças frescas em produção agroecológica" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} loading="eager" />
                </Box>
                <Box sx={{ position: 'absolute', left: 20, bottom: 20, backgroundColor: 'rgba(255,255,255,0.94)', color: '#173324', borderRadius: 3, p: 2.2, maxWidth: 280, boxShadow: '0 18px 45px rgba(0,0,0,0.16)' }}>
                  <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: '#2f7d3f', letterSpacing: '0.14em', textTransform: 'uppercase', mb: 0.5 }}>Atendimento direto</Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>Você conversa com quem produz e define o fornecimento com mais clareza.</Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box id="problema" sx={{ py: { xs: 7, md: 9 }, backgroundColor: '#fbf8f1' }}>
        <Container maxWidth="lg">
          <Stack spacing={2} sx={{ maxWidth: 760, mb: 5 }}>
            <Typography sx={{ color: '#2f7d3f', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', fontSize: '0.9rem' }}>O problema</Typography>
            <Typography sx={{ ...sectionTitleSx, mb: 0 }}>O fornecedor errado não custa só mais caro. Ele custa operação inteira.</Typography>
            <Typography sx={{ fontSize: '1.04rem', color: '#4c614f', lineHeight: 1.8 }}>
              Quando a hortaliça chega velha, o padrão oscila ou a entrega não acompanha a rotina, o seu negócio sofre em três pontos: desperdício, imagem e margem. A solução não é comprar mais. É comprar melhor, com consistência e confiança.
            </Typography>
          </Stack>
          <Grid container spacing={3}>
            {painPoints.map((item) => (
              <Grid item xs={12} md={4} key={item.title}>
                <Box sx={{ height: '100%', borderRadius: 4, backgroundColor: 'white', p: 3.5, boxShadow: '0 10px 35px rgba(24,56,31,0.06)', border: '1px solid rgba(20,60,29,0.08)', transition: 'transform 0.25s ease, box-shadow 0.25s ease', '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 18px 45px rgba(24,56,31,0.12)' } }}>
                  <Typography sx={{ fontSize: '1.15rem', fontWeight: 700, color: '#173324', mb: 1 }}>{item.title}</Typography>
                  <Typography sx={{ color: '#5e6b63', lineHeight: 1.75 }}>{item.description}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box id="solucao" sx={{ py: { xs: 7, md: 9 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={5} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: '0 16px 60px rgba(24,56,31,0.12)', border: '1px solid rgba(24,56,31,0.08)' }}>
                <img src={deliveryImage} alt="Entrega de hortaliças frescas com cuidado para um estabelecimento" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} loading="lazy" />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack spacing={2.5}>
                <Typography sx={{ color: '#2f7d3f', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', fontSize: '0.9rem' }}>A solução</Typography>
                <Typography sx={{ ...sectionTitleSx, mb: 0 }}>Uma parceria que respeita a operação do seu negócio.</Typography>
                <Typography sx={{ fontSize: '1.02rem', color: '#4c614f', lineHeight: 1.85 }}>
                  A Verde Musgo Natural trabalha com produção local, colheita perto da entrega, pequeno volume e atendimento direto com o produtor. O resultado é um fornecedor mais próximo, mais preciso e mais confiável para quem não pode correr risco.
                </Typography>
                <Stack spacing={1.2}>
                  {[
                    'Produção agroecológica certificada, sem agrotóxicos.',
                    'Entrega flexível para o seu calendário comercial.',
                    'Constância de fornecimento com padrão, frescor e aparência.',
                    'Resposta rápida pelo WhatsApp para decisões sem demora.',
                  ].map((item) => (
                    <Box key={item} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                      <VerifiedIcon sx={{ color: '#2f7d3f', mt: 0.25 }} />
                      <Typography sx={{ color: '#334b3d', lineHeight: 1.7 }}>{item}</Typography>
                    </Box>
                  ))}
                </Stack>
                <CTAButton text="Conversar com o produtor" type="whatsapp" variant="whatsapp" message={WHATSAPP_MESSAGE} size="large" />
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box id="beneficios" sx={{ py: { xs: 7, md: 9 }, backgroundColor: '#f4f8ee' }}>
        <Container maxWidth="lg">
          <Stack spacing={2} sx={{ maxWidth: 760, mb: 5 }}>
            <Typography sx={{ color: '#2f7d3f', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', fontSize: '0.9rem' }}>Benefícios</Typography>
            <Typography sx={{ ...sectionTitleSx, mb: 0 }}>O que muda quando a sua hortaliça passa a ser tratada como parte da operação.</Typography>
            <Typography sx={{ fontSize: '1.02rem', color: '#4c614f', lineHeight: 1.8 }}>
              Estamos falando de mais do que frescor. Estamos falando de menor desperdício, melhor apresentação, menos improviso e uma relação comercial mais previsível.
            </Typography>
          </Stack>
          <Grid container spacing={3}>
            {benefits.map((benefit) => {
              const Icon = benefit.icon;
              return (
                <Grid item xs={12} sm={6} lg={4} key={benefit.title}>
                  <Box sx={{ height: '100%', borderRadius: 4, backgroundColor: 'white', p: 3, boxShadow: '0 10px 30px rgba(24,56,31,0.05)', border: '1px solid rgba(24,56,31,0.08)', transition: 'transform 0.25s ease, box-shadow 0.25s ease', '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 16px 40px rgba(24,56,31,0.1)' } }}>
                    <Box sx={{ width: 48, height: 48, borderRadius: '50%', backgroundColor: '#eef8e8', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                      <Icon sx={{ color: '#2f7d3f', fontSize: 24 }} />
                    </Box>
                    <Typography sx={{ fontSize: '1.08rem', fontWeight: 700, color: '#173324', mb: 1 }}>{benefit.title}</Typography>
                    <Typography sx={{ color: '#5e6b63', lineHeight: 1.75 }}>{benefit.description}</Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>

      <Box id="como-funciona" sx={{ py: { xs: 7, md: 9 } }}>
        <Container maxWidth="lg">
          <Stack spacing={2} sx={{ maxWidth: 760, mb: 5 }}>
            <Typography sx={{ color: '#2f7d3f', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', fontSize: '0.9rem' }}>Como funciona</Typography>
            <Typography sx={{ ...sectionTitleSx, mb: 0 }}>Do campo ao seu estabelecimento, sem improviso.</Typography>
            <Typography sx={{ fontSize: '1.02rem', color: '#4c614f', lineHeight: 1.8 }}>
              O processo é simples e transparente. Você entra em contato, planejamos juntos, produzimos com padrão e entregamos com regularidade.
            </Typography>
          </Stack>
          <Grid container spacing={2.5}>
            {steps.map((step) => (
              <Grid item xs={12} md={6} lg={2.4} key={step.title}>
                <Box sx={{ height: '100%', borderRadius: 4, backgroundColor: 'white', p: 3, boxShadow: '0 10px 30px rgba(24,56,31,0.06)', border: '1px solid rgba(24,56,31,0.08)' }}>
                  <Typography sx={{ fontWeight: 700, color: '#2f7d3f', mb: 1 }}>{step.title}</Typography>
                  <Typography sx={{ color: '#5e6b63', lineHeight: 1.75 }}>{step.description}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
            <CTAButton text="Solicitar meu primeiro pedido" type="whatsapp" variant="whatsapp" message={WHATSAPP_MESSAGE} size="large" />
          </Box>
        </Container>
      </Box>

      <Box id="produtos" sx={{ py: { xs: 7, md: 9 }, backgroundColor: '#fbf8f1' }}>
        <Container maxWidth="lg">
          <Stack spacing={2} sx={{ maxWidth: 760, mb: 5 }}>
            <Typography sx={{ color: '#2f7d3f', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', fontSize: '0.9rem' }}>Produtos</Typography>
            <Typography sx={{ ...sectionTitleSx, mb: 0 }}>Hortaliças com presença, sabor e padrão para a sua operação.</Typography>
            <Typography sx={{ fontSize: '1.02rem', color: '#4c614f', lineHeight: 1.8 }}>
              Trabalhamos com variedades que atendem desde salada até preparação mais técnica, sempre com foco em qualidade e confiabilidade.
            </Typography>
          </Stack>
          <Grid container spacing={2.5}>
            {products.map((product) => (
              <Grid item xs={12} sm={6} lg={3} key={product.name}>
                <Box sx={{ height: '100%', borderRadius: 4, backgroundColor: 'white', p: 3, boxShadow: '0 10px 30px rgba(24,56,31,0.05)', border: '1px solid rgba(24,56,31,0.08)', transition: 'transform 0.25s ease, box-shadow 0.25s ease', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 14px 35px rgba(24,56,31,0.08)' } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.2 }}>
                    <AgricultureIcon sx={{ color: '#2f7d3f' }} />
                    <Typography sx={{ fontWeight: 700, color: '#173324' }}>{product.name}</Typography>
                  </Box>
                  <Typography sx={{ color: '#5e6b63', lineHeight: 1.75 }}>{product.description}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
            <CTAButton text="Ver disponibilidade de produtos" type="whatsapp" variant="whatsapp" message={WHATSAPP_MESSAGE} size="large" />
          </Box>
        </Container>
      </Box>

      <Box id="quem-atendemos" sx={{ py: { xs: 7, md: 9 } }}>
        <Container maxWidth="lg">
          <Stack spacing={2} sx={{ maxWidth: 760, mb: 5 }}>
            <Typography sx={{ color: '#2f7d3f', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', fontSize: '0.9rem' }}>Quem atendemos</Typography>
            <Typography sx={{ ...sectionTitleSx, mb: 0 }}>Estabelecimentos que valorizam qualidade, regularidade e relacionamento próximo.</Typography>
          </Stack>
          <Grid container spacing={2.5}>
            {sectors.map((sector) => {
              const Icon = sector.icon;
              return (
                <Grid item xs={12} sm={6} lg={4} key={sector.title}>
                  <Box sx={{ height: '100%', borderRadius: 4, backgroundColor: 'white', p: 3, boxShadow: '0 10px 30px rgba(24,56,31,0.05)', border: '1px solid rgba(24,56,31,0.08)' }}>
                    <Box sx={{ width: 48, height: 48, borderRadius: '50%', backgroundColor: '#eef8e8', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                      <Icon sx={{ color: '#2f7d3f', fontSize: 24 }} />
                    </Box>
                    <Typography sx={{ fontWeight: 700, color: '#173324', mb: 1 }}>{sector.title}</Typography>
                    <Typography sx={{ color: '#5e6b63', lineHeight: 1.75 }}>{sector.description}</Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
          <Box sx={{ mt: 4, borderRadius: 4, background: 'linear-gradient(90deg, #173324 0%, #2b5c2f 100%)', color: 'white', p: { xs: 3, md: 4 }, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2 }}>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', mb: 0.5 }}>Abrangência local</Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.8)' }}>Atendemos Embu-Guaçu, Zona Sul e Zona Oeste de São Paulo com entregas planejadas e presença local.</Typography>
            </Box>
            <Chip label="Embu-Guaçu • Zona Sul SP • Zona Oeste SP" sx={{ backgroundColor: 'rgba(255,255,255,0.16)', color: 'white', fontWeight: 600, px: 1.2 }} />
          </Box>
        </Container>
      </Box>

      <Box id="depoimentos" sx={{ py: { xs: 7, md: 9 }, backgroundColor: '#f4f8ee' }}>
        <Container maxWidth="lg">
          <Stack spacing={2} sx={{ maxWidth: 760, mb: 5 }}>
            <Typography sx={{ color: '#2f7d3f', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', fontSize: '0.9rem' }}>Depoimentos</Typography>
            <Typography sx={{ ...sectionTitleSx, mb: 0 }}>Parceiros que enxergam a diferença na rotina.</Typography>
          </Stack>
          <Grid container spacing={3}>
            {testimonials.map((testimonial) => (
              <Grid item xs={12} md={4} key={testimonial.name}>
                <Box sx={{ height: '100%', borderRadius: 4, backgroundColor: 'white', p: 3.5, boxShadow: '0 10px 30px rgba(24,56,31,0.05)', border: '1px solid rgba(24,56,31,0.08)' }}>
                  <Typography sx={{ fontSize: '2rem', lineHeight: 1, color: '#2f7d3f', mb: 1.5 }}>“</Typography>
                  <Typography sx={{ color: '#5e6b63', lineHeight: 1.8, mb: 2 }}>{testimonial.quote}</Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Typography sx={{ fontWeight: 700, color: '#173324' }}>{testimonial.name}</Typography>
                  <Typography sx={{ color: '#5e6b63', fontSize: '0.95rem' }}>{testimonial.role} • {testimonial.place}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box id="sobre" sx={{ py: { xs: 7, md: 9 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={5} alignItems="center">
            <Grid item xs={12} md={6}>
              <Stack spacing={2.5}>
                <Typography sx={{ color: '#2f7d3f', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', fontSize: '0.9rem' }}>Sobre a Verde Musgo Natural</Typography>
                <Typography sx={{ ...sectionTitleSx, mb: 0 }}>Uma produção familiar, próxima e comprometida com a qualidade real.</Typography>
                <Typography sx={{ fontSize: '1.01rem', color: '#4c614f', lineHeight: 1.85 }}>
                  A Verde Musgo Natural nasceu de uma forma simples de ver o agro: produzir com respeito à terra, cuidar do cliente e oferecer hortaliças que tenham verdade no sabor, na aparência e na regularidade. Não vendemos apenas folhas. Vendemos confiança, rotina e um fornecedor que entende a operação do seu negócio.
                </Typography>
                <Stack spacing={1.2}>
                  {[
                    'Honestidade comercial e comunicação direta.',
                    'Cuidado com cada etapa da produção e da entrega.',
                    'Compromisso com a sustentabilidade e a segurança alimentar.',
                    'Presença local e relacionamento próximo com o cliente.',
                  ].map((item) => (
                    <Box key={item} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                      <LocalFloristIcon sx={{ color: '#2f7d3f', mt: 0.25 }} />
                      <Typography sx={{ color: '#334b3d', lineHeight: 1.7 }}>{item}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: '0 16px 60px rgba(24,56,31,0.12)', border: '1px solid rgba(24,56,31,0.08)' }}>
                <img src={aboutImage} alt="Produção familiar de hortaliças em ambiente agroecológico" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} loading="lazy" />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box id="faq" sx={{ py: { xs: 7, md: 9 }, backgroundColor: '#fbf8f1' }}>
        <Container maxWidth="lg">
          <Stack spacing={2} sx={{ maxWidth: 760, mb: 5 }}>
            <Typography sx={{ color: '#2f7d3f', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', fontSize: '0.9rem' }}>FAQ</Typography>
            <Typography sx={{ ...sectionTitleSx, mb: 0 }}>Perguntas frequentes sobre fornecimento, entrega e qualidade.</Typography>
          </Stack>
          <Stack spacing={1.5}>
            {faqItems.map((item, index) => {
              const isExpanded = expandedFaq === `panel${index}`;
              return (
                <Accordion
                  key={item.question}
                  disableGutters
                  elevation={0}
                  expanded={isExpanded}
                  onChange={() => setExpandedFaq(isExpanded ? false : `panel${index}`)}
                  sx={{ borderRadius: 3, border: '1px solid rgba(24,56,31,0.1)', '&:before': { display: 'none' } }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls={`faq-panel-${index}`} id={`faq-header-${index}`} sx={{ px: 2.5, py: 1 }}>
                    <Typography sx={{ fontWeight: 700, color: '#173324' }}>{item.question}</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 2.5, pb: 2.5 }}>
                    <Typography sx={{ color: '#5e6b63', lineHeight: 1.8 }}>{item.answer}</Typography>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Stack>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 7, md: 9 }, background: 'linear-gradient(135deg, #173324 0%, #285b2d 100%)', color: 'white' }}>
        <Container maxWidth="md">
          <Stack spacing={3} alignItems="center" textAlign="center">
            <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', fontSize: '0.9rem' }}>CTA final</Typography>
            <Typography sx={{ ...sectionTitleSx, color: 'white', mb: 0 }}>Pronto para ter um fornecedor de confiança para a sua operação?</Typography>
            <Typography sx={{ fontSize: '1.02rem', color: 'rgba(255,255,255,0.82)', lineHeight: 1.8, maxWidth: 660 }}>
              Comece com um pedido pequeno, troque mensagens rápidas e construa uma relação comercial mais estável para o futuro.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <CTAButton text="Iniciar fornecimento agora" type="whatsapp" variant="whatsapp" message={WHATSAPP_MESSAGE} size="large" />
              <CTAButton text="Solicitar tabela de preços" type="whatsapp" variant="outlined" message={WHATSAPP_MESSAGE} size="large" />
            </Stack>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default HortalicasPage;
